import { ChatBubbleLeftEllipsisIcon, TruckIcon } from "@heroicons/react/20/solid";
import type { ReactNode } from "react";
import { OtsBadge } from "../components/OtsBadge";
import {
  formatActivityDuration,
  formatActivityMoney,
  formatActivityQuantity,
  formatActivityTime,
} from "./driver-activity-format";
import type { ActivityShiftFacts, ActivityTotals } from "./driver-activity-view-model";
import { ActivityDayOffset } from "./ActivityDayOffset";
import { OtsDateTime } from "../components/OtsDateTime";

export type ActivityShiftRowProps = {
  /** Start opens a shift's activity, end closes it; they are separate rows, as in the WaterGo viewer. */
  variant: "start" | "end";
  /** Missing for activity recorded outside any shift, which gets a start row only. */
  shift?: ActivityShiftFacts;
  /** The shift's ticket totals, summed up on its end row. */
  totals?: ActivityTotals;
  timeZone?: string;
  /** The day being viewed; times on other days get a +1d / −1d badge. */
  day?: Date;
  /** "Now", for the running length of an open shift. */
  now?: number;
  showRevenue?: boolean;
  /** Only some rate models are counted, so the end row sums its tickets rather than the server's shift figures. */
  rateModelsNarrowed?: boolean;
  /** Costing the shift, at the end of its end row's money. */
  costing?: ReactNode;
  /** Shown on the start row, e.g. the shift's weakest audit. */
  audit?: ReactNode;
  /** Buttons at the end of the start row, such as editing the shift. */
  actions?: ReactNode;
};

const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
  <span role="gridcell" className="whitespace-nowrap"><span className="opacity-70">{label} </span><strong className="font-semibold">{children}</strong></span>
);

/** Billable times are only worth showing when they are not simply the shift's own, to the minute. */
const differs = (billable: number | undefined, actual: number | undefined): billable is number =>
  billable != null && actual != null && Math.floor(billable / 60_000) !== Math.floor(actual / 60_000);

const rowClass = "flex min-h-8 items-center gap-x-4 gap-y-1 border-b border-[var(--ots-outline-variant)] last:border-b-0 py-[var(--da-pad,6px)] pl-3 pr-3 text-xs tabular-nums";

/** The Start Shift (green) and End Shift (red) rows that bracket a shift's activity. */
export const ActivityShiftRow = ({ variant, shift, totals, timeZone, day, now = Date.now(), showRevenue = false, rateModelsNarrowed = false, costing, audit, actions }: ActivityShiftRowProps) => {
  // Beside the start time rather than at the row's far end, which can sit past the edge of a wide grid.
  const actionCell = actions && <span role="gridcell" className="-my-1 flex items-center gap-0.5">{actions}</span>;
  if (!shift) {
    return variant === "start"
      ? <div role="row" data-shift="none" className={[rowClass, "bg-[var(--ots-neutral-container)]"].join(" ")}>
        <span role="gridcell"><OtsBadge size="compact">Outside a shift</OtsBadge></span>
        {actionCell}
      </div>
      : null;
  }

  if (variant === "start") {
    const equipment = [shift.vehicleName, shift.trailerName].filter(Boolean).join(" / ");
    return (
      <div role="row" data-shift="start" className={[rowClass, "bg-[var(--ots-success-container)] text-[var(--ots-on-success-container)] shadow-[inset_4px_0_0_var(--ots-success)]"].join(" ")}>
        <span role="gridcell" className="flex items-center gap-2">
          {audit}
          <OtsBadge tone="success" size="compact">Start Shift</OtsBadge>
          <strong className="text-sm font-semibold"><OtsDateTime value={shift.startDttm} format="time" timeZone={timeZone}/></strong>
          <ActivityDayOffset time={shift.startDttm} day={day} timeZone={timeZone}/>
        </span>
        {actionCell}
        {shift.driverVerified && <span role="gridcell"><OtsBadge tone="success" size="compact">Driver verified</OtsBadge></span>}
        {shift.driverDenied && <span role="gridcell"><OtsBadge tone="danger" size="compact">Driver denied</OtsBadge></span>}
        {shift.holdingRecord && <span role="gridcell"><OtsBadge size="compact">Holding</OtsBadge></span>}
        {shift.shiftTypeName && <Fact label="Type">{shift.shiftTypeName}</Fact>}
        {differs(shift.billableStartDttm, shift.startDttm) && <Fact label="Billable from">{formatActivityTime(shift.billableStartDttm, timeZone)}</Fact>}
        {!!shift.preBillableDttm && <Fact label="Pre-billable">{formatActivityDuration(shift.preBillableDttm * 60_000)}</Fact>}
        {shift.startGeofenceEvent?.outDttm != null && <Fact label="Departed">{formatActivityTime(shift.startGeofenceEvent.outDttm, timeZone)}</Fact>}
        {equipment && <span role="gridcell" className="flex items-center gap-1"><TruckIcon aria-hidden="true" className="size-3.5"/>{equipment}</span>}
        {shift.trailerCompanyName && <Fact label="Provider">{shift.trailerCompanyName}</Fact>}
        {shift.startOdometer != null && <Fact label="Odometer">{formatActivityQuantity(shift.startOdometer, shift.odometerUom)}</Fact>}
      </div>
    );
  }

  const open = shift.endDttm == null;
  const mileage = (shift.loadedMileage ?? 0) + (shift.unloadedMileage ?? 0) || totals?.mileage;

  return (
    <div
      role="row"
      data-shift={open ? "open" : "end"}
      className={[rowClass, open
        ? "bg-[var(--ots-neutral-container)] shadow-[inset_4px_0_0_var(--ots-outline)]"
        : "bg-[var(--ots-danger-container)] text-[var(--ots-on-danger-container)] shadow-[inset_4px_0_0_var(--ots-error)]"].join(" ")}>
      <span role="gridcell" className="flex items-center gap-2">
        {open
          ? <OtsBadge tone="success" size="compact" dot>Shift in progress</OtsBadge>
          : <>
            <OtsBadge tone="danger" size="compact">End Shift</OtsBadge>
            <strong className="text-sm font-semibold"><OtsDateTime value={shift.endDttm} format="time" timeZone={timeZone}/></strong>
            <ActivityDayOffset time={shift.endDttm} day={day} timeZone={timeZone}/>
          </>}
      </span>
      {!open && differs(shift.billableEndDttm, shift.endDttm) && <Fact label="Billable to">{formatActivityTime(shift.billableEndDttm, timeZone)}</Fact>}
      {!!shift.postBillableDttm && <Fact label="Post-billable">{formatActivityDuration(shift.postBillableDttm * 60_000)}</Fact>}
      {shift.endGeofenceEvent?.outDttm != null && <Fact label="Arrived">{formatActivityTime(shift.endGeofenceEvent.outDttm, timeZone)}</Fact>}
      <Fact label="Duration">{formatActivityDuration(Math.max((shift.endDttm ?? now) - shift.startDttm, 0))}</Fact>
      {totals && <>
        <Fact label="Loads">{totals.loads}</Fact>
        <Fact label="Qty">{formatActivityQuantity(totals.quantity, totals.quantityUom)}</Fact>
      </>}
      {!!mileage && <Fact label="Miles">{formatActivityQuantity(mileage)}</Fact>}
      {shift.endOdometer != null && <Fact label="Odometer">{formatActivityQuantity(shift.endOdometer, shift.odometerUom)}</Fact>}
      {showRevenue && totals && <>
        {/* The server's shift figures include shift-level charges and every rate model; the ticket sums are the
            fallback, and all there is to go on once only some rate models are counted. */}
        <Fact label="Cost">{formatActivityMoney((!rateModelsNarrowed && shift.cost) || totals.cost)}</Fact>
        <Fact label="Revenue">{formatActivityMoney((!rateModelsNarrowed && shift.revenue) || totals.revenue)}</Fact>
      </>}
      {!!shift.messages?.length && (
        <span role="gridcell" className="flex items-center gap-1 text-[var(--ots-primary)]">
          <ChatBubbleLeftEllipsisIcon aria-hidden="true" className="size-3.5"/>
          {shift.messages[shift.messages.length - 1].message}
        </span>
      )}
      {/* At the far end, where it can't be mistaken for one of the shift's figures; pinned to the visible edge,
          on the end row's own color, while a wide grid scrolls sideways. */}
      {showRevenue && costing && (
        <span role="gridcell" className="sticky right-0 -my-1 -mr-3 ml-auto flex items-center gap-1.5 bg-[var(--ots-danger-container)] py-1 pl-2 pr-3">
          {costing}
        </span>
      )}
    </div>
  );
};
